import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import {
  Landing,
  Dashboard,
  Analysis,
  Scanner,
  Watchlist,
  Simulator,
  Research,
  Settings,
  Login,
  APIDocs,
  Terms,
  Privacy,
  Disclaimer,
  Chat,
} from './pages';
import { ChatWidget } from './components/chat/ChatWidget';
import { useThemeStore, useAuthStore, useMarketStore } from './store';
import { getCurrentUser, getSubscriptionStatus, getUserWatchlists } from './utils/api';
import type { SubscriptionTierType } from './types';

// Paid tiers that have watchlist access
const PAID_TIERS = ['starter', 'early_supporter', 'pro', 'enterprise'];

// Check if we're on the app subdomain or localhost (for development)
// In production: true for app.stocksentiment.io
// In development: true for localhost (to test app functionality)
const isAppSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('app.') || hostname === 'localhost';
};

// Get the app domain URL for redirects
const getAppDomainUrl = () => {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:3000';
  }
  return 'https://app.stocksentiment.io';
};

// Component to redirect from main domain /app/* to app subdomain
const RedirectToAppSubdomain = () => {
  useEffect(() => {
    // Get the path after /app/
    const path = window.location.pathname.replace('/app', '') || '/';
    const appUrl = getAppDomainUrl() + path + window.location.search + window.location.hash;
    window.location.href = appUrl;
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
      <div className="text-[var(--text-secondary)]">Redirecting to app...</div>
    </div>
  );
};

function App() {
  const { theme } = useThemeStore();
  const { login, isAuthenticated, updateSubscription, user } = useAuthStore();
  const { setUserWatchlists, setActiveWatchlist } = useMarketStore();

  // Handle OAuth callback - check for token in URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');

      if (accessToken) {
        // Fetch user info with the token and store it
        const fetchUser = async () => {
          try {
            // Temporarily set token to make authenticated request
            localStorage.setItem('sentimentedge-auth', JSON.stringify({
              state: { token: accessToken }
            }));

            const user = await getCurrentUser();
            login(user, accessToken);

            // Clean up URL hash
            window.history.replaceState(null, '', window.location.pathname);
          } catch (error) {
            console.error('OAuth callback error:', error);
            localStorage.removeItem('sentimentedge-auth');
          }
        };
        fetchUser();
      }
    }
  }, [login]);

  // Sync subscription status on app load when authenticated
  // This ensures tier is up-to-date even if webhooks were missed
  useEffect(() => {
    if (!isAuthenticated) return;

    const syncSubscriptionStatus = async () => {
      try {
        const status = await getSubscriptionStatus();
        updateSubscription(
          status.tier as SubscriptionTierType,
          status.status as 'active' | 'past_due' | 'cancelled' | 'trialing' | 'unpaid'
        );
      } catch (error) {
        // Silently fail - user can still use the app with cached tier
        console.warn('Failed to sync subscription status:', error);
      }
    };

    syncSubscriptionStatus();
  }, [isAuthenticated, updateSubscription]);

  // Sync watchlists for paid users
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const isPaid = PAID_TIERS.includes(user.subscription_tier);

    if (isPaid) {
      // Fetch watchlists for paid users
      getUserWatchlists()
        .then(({ watchlists }) => {
          setUserWatchlists(watchlists);
          const defaultList = watchlists.find(w => w.is_default) || watchlists[0];
          if (defaultList) {
            setActiveWatchlist(defaultList.id);
          }
        })
        .catch((error) => {
          // Silently fail - user can still use local watchlist
          console.warn('Failed to sync watchlists:', error);
        });
    }
  }, [isAuthenticated, user?.subscription_tier, setUserWatchlists, setActiveWatchlist]);

  // Apply theme on mount and changes
  useEffect(() => {
    const root = document.documentElement;
    let effectiveTheme = theme;

    if (theme === 'system') {
      effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    root.setAttribute('data-theme', effectiveTheme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Determine which routes to render based on subdomain
  const onAppSubdomain = isAppSubdomain();

  return (
    <>
      <BrowserRouter>
        <Routes>
          {onAppSubdomain ? (
            <>
              {/* App subdomain routes - app is at root */}
              <Route path="/login" element={<Login />} />

              {/* Protected app routes at root */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="scanner" element={<Scanner />} />
                <Route path="analysis" element={<Analysis />} />
                <Route path="fundamentals" element={<Navigate to="/analysis" replace />} />
                <Route path="chat" element={<Chat />} />
                <Route path="watchlist" element={<Watchlist />} />
                <Route path="simulator" element={<Simulator />} />
                <Route path="research" element={<Research />} />
                <Route path="performance" element={<Navigate to="/research" replace />} />
                <Route path="settings" element={<Settings />} />
                <Route path="api-docs" element={<APIDocs />} />
              </Route>

              {/* Redirect old /app paths to root on app subdomain */}
              <Route path="/app/*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              {/* Main domain routes - landing page */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/disclaimer" element={<Disclaimer />} />

              {/* Redirect /app/* to app subdomain */}
              <Route
                path="/app/*"
                element={
                  <RedirectToAppSubdomain />
                }
              />
            </>
          )}
        </Routes>
        {/* Floating chat widget - only show when authenticated and on app pages */}
        {isAuthenticated && <ChatWidget />}
      </BrowserRouter>
    </>
  );
}

export default App;
